import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Brain, CheckCircle2, AlertTriangle, Info, RefreshCw, Sparkles } from "lucide-react";

const ICONS = {
  ok: <CheckCircle2 size={15} className="text-emerald-500 flex-none mt-0.5" />,
  warn: <AlertTriangle size={15} className="text-amber-500 flex-none mt-0.5" />,
  info: <Info size={15} className="text-sky-500 flex-none mt-0.5" />,
};

export const OrderPlanDialog = ({ order, onClose }) => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh = false) => {
    setBusy(true);
    api.get(`/intel/order-plan/${order.id}${refresh ? "?refresh=1" : ""}`)
      .then((r) => setPlan(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { if (order) { setPlan(null); load(); } }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!order) return null;
  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="order-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Brain size={17} className="text-[#3B82F6]" /> Order Brain — {order.number}
            {plan?.customer && <span className="text-sm font-normal text-slate-500">· {plan.customer}</span>}
            {plan && plan.warnings > 0 && (
              <span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{plan.warnings} to fix</span>
            )}
          </DialogTitle>
        </DialogHeader>
        {!plan && <div className="text-sm text-slate-400 py-8 text-center">Thinking through this order…</div>}
        {plan && (
          <div className="space-y-4">
            {!plan.brief && (
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-400 flex items-center gap-2">
                AI brief unavailable right now
                <button onClick={() => load(true)} disabled={busy} className="text-[#3B82F6] font-semibold hover:underline">Retry</button>
              </div>
            )}
            {plan.brief && (
              <div className="rounded-xl bg-gradient-to-r from-[#EFF6FF] to-[#EEF2FF] border border-blue-100 p-3.5 text-[13px] text-slate-700 leading-relaxed" data-testid="order-plan-brief">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#3B82F6] mb-1.5">
                  <Sparkles size={11} /> AI Brief
                  <button onClick={() => load(true)} disabled={busy} className="ml-auto text-slate-400 hover:text-[#3B82F6]" title="Regenerate">
                    <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
                  </button>
                </div>
                {plan.brief}
              </div>
            )}
            <div className="space-y-2" data-testid="order-plan-checks">
              {plan.checks.map((c, i) => (
                <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${c.status === "warn" ? "border-amber-200 bg-amber-50/50" : "border-slate-100 bg-white"}`}>
                  {ICONS[c.status] || ICONS.info}
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-slate-800">{c.title}</div>
                    {c.detail && <div className="text-xs text-slate-500 mt-0.5">{c.detail}</div>}
                  </div>
                  {c.link && <Link to={c.link} onClick={onClose} className="text-[11px] font-semibold text-[#3B82F6] hover:underline flex-none mt-0.5">Open →</Link>}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
